/**
 * Open the AI-polished CCL draft view for a matter, by matter reference.
 *
 * Background (Vanessa, 20 May 2026): the "Open CCL →" action on the
 * pipeline accordion was routing fee earners to
 *   /pre-caseminder?matter={ref}&tab=ccl
 * That route is the *intake registration* form. It ignores `tab=ccl` and
 * just renders its default "Before we begin / SharePoint?" landing page,
 * which has nothing to do with the CCL the user expected to edit. The
 * comment on PreCaseMinder.jsx:69-74 even acknowledges this is a stop-
 * gap — the page surfaces an "Edit FORF" banner and lets users escape
 * back to /forf/{ref}, but offers no path to the actual CCL editor.
 *
 * The real CCL editor (CclEditor.jsx — AI Once-Over, inline amendments,
 * save-and-confirm) is mounted inside the matter detail page at
 * /submission/{submission_id}. We therefore need to
 * resolve matter_reference → submission_id before we can navigate to
 * the right place.
 *
 * Trust-context: this helper NEVER falls back to /pre-caseminder. If
 * the lookup fails (no match, multiple matches, network/HTTP error) the
 * caller's `onError` is invoked with a human-readable reason and NO
 * navigation happens. We refuse to silently land the user on a route
 * that doesn't show the CCL — that is the exact bug being fixed here.
 *
 * @param {object}   deps
 * @param {string}   deps.matterRef         The BL.XXX.YYY.NNNN reference.
 * @param {object}   deps.axios             Axios instance / API client.
 * @param {string}   deps.apiBase           Base URL for the hub API (e.g. API constant).
 * @param {(path: string) => void} deps.navigate  react-router navigate function.
 * @param {(msg: string, err?: Error) => void} [deps.onError]
 *        Called when no submission can be resolved. Defaults to console.error
 *        + window.alert so failures are never silent.
 * @returns {Promise<void>}
 */
export async function openMatterCcl({ matterRef, axios, apiBase, navigate, onError }) {
  const ref = (matterRef || "").trim();
  const handleError = onError || ((msg, err) => {
    // eslint-disable-next-line no-console
    console.error("[openMatterCcl]", msg, err || "");
    if (typeof window !== "undefined" && typeof window.alert === "function") {
      window.alert(msg);
    }
  });

  if (!ref) {
    handleError("Cannot open CCL — no matter reference supplied.");
    return;
  }

  let resp;
  try {
    resp = await axios.get(`${apiBase}/submissions`, {
      params: { matter_reference: ref, limit: 50 },
    });
  } catch (err) {
    handleError(
      `Could not look up CCL for ${ref} — ${err?.response?.status || "network error"}.`,
      err
    );
    return;
  }

  const rows = Array.isArray(resp?.data) ? resp.data : [];
  if (rows.length === 0) {
    handleError(
      `No matter found for ${ref}. The CCL draft lives on the matter's submission record; ` +
      `if this matter was created outside CaseMinder you'll need to register it via Pre-CaseMinder first.`
    );
    return;
  }

  // The matter's own record is the matter_intake / pre_caseminder row (older
  // rows: matter_opening / onboarding). Every other form filed against the
  // matter (invoice request, payment request, file closure...) shares its
  // matter_reference, and the list endpoint sorts newest first.
  //
  // Liezel, 2 Nov 2026, BL.RS.006.1202: "Open CCL" landed her on an Invoice
  // Request. The lookup asked for only 2 rows and fell back to the first row
  // when neither was the matter, so once two newer forms had been filed the
  // matter row was never seen and the newest invoice request was opened
  // instead. Ask for enough rows to reach the matter, and never open a row
  // that is not the matter.
  const matterFormTypes = new Set(["matter_intake", "pre_caseminder", "matter_opening", "onboarding"]);
  // BL.VEC.006.1098 (3 Nov 2026): older intake rows carry their form type only
  // under data.form_type, with none at the top level, so read both.
  const formTypeOf = (r) => r?.form_type || r?.data?.form_type;
  const matterRow = rows.find((r) => matterFormTypes.has(formTypeOf(r)));
  if (!matterRow) {
    handleError(
      `Could not find the matter record for ${ref}, so the CCL cannot be opened from here. ` +
      `Open the matter from Matters and scroll to the client care letter.`
    );
    return;
  }
  const submissionId = matterRow?.id;

  if (!submissionId) {
    handleError(
      `Matter ${ref} resolved but the submission record is missing its id — refusing to navigate.`
    );
    return;
  }

  // ?tab=ccl is preserved as a hint to the matter detail page so it can
  // auto-focus the CCL editor once that wiring lands (out of scope here).
  navigate(`/submission/${encodeURIComponent(submissionId)}?tab=ccl`);
}
